import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import history from './history';
import * as URL from './data/routes-constants';
import LoaderContainer from './components/reusable-ui/common-loader';
import { getLocalStorage } from './utils';
import { AUTH_TOKEN, IS_LOGGED_IN } from './data/local-storage-constants';
import { FormProvider } from './features/credit-report-apply/component/form-provider';
import { isMobileDevice } from './helpers/device-info-helper';
import UnsupportedInfo from './components/info/UnsupportedInfo';
import { ANDROID_FEATURE_VERSION_SUPPORT } from './data/constants';
import { isFlipkartFlow } from './features/credit-report-apply/helper';

const HomePage = lazy(() => import('./pages/home-page')),
  BusinessLoanPage = lazy(() => import('./pages/business-loan')),
  PersonalLoanPage = lazy(() => import('./pages/personal-loan')),
  AboutUsPage = lazy(() => import('./pages/about-us')),
  ContactUsPage = lazy(() => import('./pages/contact-us')),
  PrivacyPolicyPage = lazy(() => import('./pages/privacy-policy')),
  TermsofUsePage = lazy(() => import('./pages/terms-of-use')),
  DisclaimerPage = lazy(() => import('./pages/disclaimer')),
  ThankYouPage = lazy(() => import('./pages/thank-you')),
  IntellectualPropertyPage = lazy(
    () => import('./pages/intellectual-property')
  ),
  GrievanceRedressalPage = lazy(() => import('./pages/grievance-redressal')),
  GrievanceRedressalBusinessPage = lazy(
    () => import('./pages/grievance-redressal-business')
  ),
  GrievanceRedressalPersonalPage = lazy(
    () => import('./pages/grievance-redressal-personal')
  ),
  CreditTnCPage = lazy(() => import('./pages/credit-terms-of-use')),
  ExperianTnCPage = lazy(() => import('./pages/experian-terms-of-use')),
  CreditReportApplyPage = lazy(() => import('./pages/credit-report-apply')),
  BLBusinessDetailsPage = lazy(
    () =>
      import('./features/credit-report-apply/component/en-business-last-step')
  ),
  BLSignUpLayout = lazy(() => import('./components/loan-onboarding-layout')),
  BLStepsPage = lazy(
    () => import('./features/credit-report-apply/component/form-steps')
  ),
  BLBankStatementPage = lazy(
    () => import('./features/credit-report-apply/component/bank-statement')
  ),
  BLLendersPage = lazy(
    () => import('./features/credit-report-apply/component/lenders')
  ),
  BLUploadDocsPage = lazy(
    () => import('./features/credit-report-apply/component/upload-documents')
  ),
  BLOffersPage = lazy(
    () => import('./features/credit-report-apply/component/unlock-offers')
  ),
  BLGSTPage = lazy(
    () => import('./features/credit-report-apply/component/en-business-gst')
  ),
  CreditReportPage = lazy(() => import('./pages/credit-report')),
  CreditFAQPage = lazy(() => import('./pages/credit-faqs')),
  CreditProfilePage = lazy(() => import('./pages/credit-profile')),
  CreditContactUs = lazy(() => import('./pages/credit-contact-us')),
  NotFoundPage = lazy(() => import('./pages/not-found'));

const BL_ROUTES = [
  {
    path: URL.BL_STEPS_ROUTE,
    element: <BLStepsPage />,
    layout: BLSignUpLayout,
    wrapWithForm: true,
    includeSteps: true,
  },
  {
    path: URL.BL_BUSINESS_DETAILS_ROUTE,
    element: <BLBusinessDetailsPage />,
    layout: BLSignUpLayout,
    wrapWithForm: true,
    includeSteps: true,
  },
  {
    path: URL.BL_BANK_STATEMENT_ROUTE,
    element: <BLBankStatementPage />,
    layout: BLSignUpLayout,
    wrapWithForm: true,
    includeSteps: true,
  },
  {
    path: URL.BL_LENDERS_ROUTE,
    element: <BLLendersPage />,
    layout: BLSignUpLayout,
    wrapWithForm: true,
    includeSteps: true,
  },
  {
    path: URL.BL_UPLOAD_DOCS_ROUTE,
    element: <BLUploadDocsPage />,
    layout: BLSignUpLayout,
    wrapWithForm: true,
    includeSteps: true,
  },
  {
    path: URL.BL_OFFERS_ROUTE,
    element: <BLOffersPage />,
    layout: BLSignUpLayout,
    wrapWithForm: true,
    includeSteps: true,
  },
];

const AppRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isFlipkart, setIsFlipkart] = useState(false);

  useEffect(() => {
    // Delay to ensure sessionStorage is populated before checking Flipkart flow
    const timer = setTimeout(() => {
      setIsFlipkart(isFlipkartFlow());
    }, 0); // Small delay to allow sessionStorage to update

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const token = getLocalStorage(AUTH_TOKEN);
    const isLoggedIn = getLocalStorage(IS_LOGGED_IN) === 'true';
    const ignoredRoutes = [
      URL.CREDIT_REPORT,
      URL.CREDIT_FAQS,
      URL.CREDIT_PROFILE,
      URL.CREDIT_CONTACT_US,
    ];
    const FLIgnoredRoutes = [URL.CREDIT_REPORT_APPLY_ROUTE];

    if (
      !token &&
      !isLoggedIn &&
      isFlipkart &&
      !FLIgnoredRoutes.includes(location.pathname)
    ) {
      navigate(URL.GST_ROUTE, { replace: true });
      return;
    }

    if (token && !ignoredRoutes.includes(location.pathname) && isLoggedIn) {
      navigate(URL.CREDIT_REPORT, { replace: true });
    }
  }, [navigate, isFlipkart]); // Now runs when isFlipkart updates

  const renderElement = ({
    element,
    layout: Layout,
    wrapWithForm,
    includeSteps,
  }) => {
    const content =
      isMobileDevice() || !includeSteps ? (
        element
      ) : (
        <>
          <BLStepsPage />
          {element}
        </>
      );

    return (
      <UnsupportedInfo
        supportedVersion={ANDROID_FEATURE_VERSION_SUPPORT.BL_KYC}>
        {wrapWithForm ? (
          <FormProvider>
            {Layout ? <Layout>{content}</Layout> : content}
          </FormProvider>
        ) : (
          content
        )}
      </UnsupportedInfo>
    );
  };

  return (
    <Suspense fallback={<LoaderContainer />}>
      <Routes history={history}>
        <Route exact path={'/'} element={<HomePage />} />
        <Route
          exact
          path={URL.BUSINESS_LOAN_PAGE_ROUTE}
          element={<BusinessLoanPage />}
        />
        <Route
          exact
          path={URL.PERSONAL_LOAN_PAGE_ROUTE}
          element={<PersonalLoanPage />}
        />
        <Route exact path={URL.ABOUT_US_PAGE_ROUTE} element={<AboutUsPage />} />
        <Route
          exact
          path={URL.CONTACT_US_PAGE_ROUTE}
          element={<ContactUsPage />}
        />
        <Route
          exact
          path={URL.PRIVACY_POLICY_PAGE_ROUTE}
          element={<PrivacyPolicyPage />}
        />
        <Route
          exact
          path={URL.TERMS_N_COND_PAGE_ROUTE}
          element={<TermsofUsePage />}
        />
        <Route
          exact
          path={URL.DISCLAIMER_PAGE_ROUTE}
          element={<DisclaimerPage />}
        />
        <Route
          exact
          path={URL.THANK_YOU_PAGE_ROUTE}
          element={<ThankYouPage />}
        />
        <Route
          exact
          path={URL.INTELLECTUAL_PROPERTY_PAGE_ROUTE}
          element={<IntellectualPropertyPage />}
        />
        <Route
          exact
          path={URL.GRIEVANCE_REDRESSAL_PAGE_ROUTE}
          element={<GrievanceRedressalPage />}
        />
        <Route
          exact
          path={URL.GRIEVANCE_REDRESSAL_BUSINESS_PAGE_ROUTE}
          element={<GrievanceRedressalBusinessPage />}
        />
        <Route
          exact
          path={URL.GRIEVANCE_REDRESSAL_PERSONAL_PAGE_ROUTE}
          element={<GrievanceRedressalPersonalPage />}
        />
        <Route
          exact
          path={URL.CREDIT_TERMS_N_COND_PAGE_ROUTE}
          element={<CreditTnCPage />}
        />
        <Route
          exact
          path={URL.EXPERIAN_TERMS_N_COND_PAGE_ROUTE}
          element={<ExperianTnCPage />}
        />

        <Route
          exact
          path={URL.CREDIT_REPORT_APPLY_ROUTE}
          element={<CreditReportApplyPage />}
        />
        {/* BL flow urls */}
        {BL_ROUTES.map(({ path, ...rest }) => (
          <Route key={path} exact path={path} element={renderElement(rest)} />
        ))}
        <Route exact path={URL.GST_ROUTE} element={<BLGSTPage />} />
        {/* BL flow urls */}
        <Route exact path={URL.CREDIT_REPORT} element={<CreditReportPage />} />
        <Route exact path={URL.CREDIT_FAQS} element={<CreditFAQPage />} />
        <Route
          exact
          path={URL.CREDIT_PROFILE}
          element={<CreditProfilePage />}
        />
        <Route
          exact
          path={URL.CREDIT_CONTACT_US}
          element={<CreditContactUs />}
        />

        {/* Render NotFoundPage for unmatched routes */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
